/* global messg */
import { kea } from 'kea'
import { put } from 'redux-saga/effects'
import PropTypes from 'prop-types'

import I18n from 'lib/i18n'
import delay from 'lib/utils/delay'
import isEmailValid from 'lib/utils/is-email-valid'
import currentUserIdSelector from 'lib/selectors/current-user-id'

import controller from './controller.rb'

export const PAGES = [
  'first-page',
  'category-location',
  'student-age',
  'start-time',
  'availability',
  'frequency',
  'location-type',
  'request',
  'thank-you'
]

export const VALIDATORS = {
  1: () => true,
  2: state => state.category && state.location,
  3: state => state.studentAge,
  4: state => state.startTime,
  5: state => state.availabilityFlexible || state.availability.length > 0,
  6: state => state.frequency,
  7: state => state.locationType,
  8: () => false,
  9: () => false
}

export default kea({
  initScene: {
    scene: 'pagesRequest',
    transform: payload => ({
      howItWorksTitles: payload.howItWorksTitles,
      categorySuggestions: payload.categorySuggestions
    })
  },

  path: () => ['scenes', 'pagesRequest', 'index'],

  actions: () => ({
    nextPage: true,
    previousPage: true,
    restart: true,
    setFlexibleAvailability: true,
    submitRequest: true,
    submitSuccess: true,
    stopSubmitting: true,
    setCategory: category => ({ category }),
    setLocation: location => ({ location }),
    setLocationType: locationType => ({ locationType }),
    setFrequency: frequency => ({ frequency }),
    setStartTime: startTime => ({ startTime }),
    setStudentAge: studentAge => ({ studentAge }),
    toggleAvailability: dayTime => ({ dayTime }),
    setPersonalNote: personalNote => ({ personalNote }),
    setFullName: fullName => ({ fullName }),
    setEmail: email => ({ email }),
    setPhone: (phone, valid, country) => ({ phone, valid, country }),
    submitFailure: errors => ({ errors }),
    getSuggestions: value => ({ value }),
    setSuggestions: ({ value, suggestions }) => ({ value, suggestions }),
    setFetchingSuggestionsStatus: bool => ({ bool })
  }),

  reducers: ({ actions }) => ({
    howItWorksTitles: [[], PropTypes.array, {}],
    page: [
      1,
      PropTypes.number,
      {
        [actions.nextPage]: state => Math.min(state + 1, 8),
        [actions.previousPage]: state => Math.max(state - 1, 1),
        [actions.submitSuccess]: () => 9,
        [actions.restart]: () => 2
      }
    ],
    availability: [
      [],
      PropTypes.array,
      {
        [actions.toggleAvailability]: (state, { dayTime }) =>
          state.indexOf(dayTime) >= 0 ? state.filter(dt => dt !== dayTime) : state.concat([dayTime]).sort(),
        [actions.setFlexibleAvailability]: () => [],
        [actions.restart]: () => []
      }
    ],
    availabilityFlexible: [
      false,
      PropTypes.bool,
      {
        [actions.toggleAvailability]: () => false,
        [actions.setFlexibleAvailability]: () => true,
        [actions.restart]: () => false
      }
    ],
    category: [
      '',
      PropTypes.string,
      {
        [actions.setCategory]: (_, { category }) => category,
        [actions.restart]: () => ''
      }
    ],
    location: [
      '',
      PropTypes.string,
      {
        [actions.setLocation]: (_, { location }) => location,
        [actions.restart]: () => ''
      }
    ],
    studentAge: [
      null,
      PropTypes.string,
      {
        [actions.setStudentAge]: (_, { studentAge }) => studentAge,
        [actions.restart]: () => null
      }
    ],
    startTime: [
      null,
      PropTypes.string,
      {
        [actions.setStartTime]: (_, { startTime }) => startTime,
        [actions.restart]: () => null
      }
    ],
    locationType: [
      null,
      PropTypes.string,
      {
        [actions.setLocationType]: (_, { locationType }) => locationType,
        [actions.restart]: () => null
      }
    ],
    frequency: [
      null,
      PropTypes.string,
      {
        [actions.setFrequency]: (_, { frequency }) => frequency,
        [actions.restart]: () => null
      }
    ],
    personalNote: [
      '',
      PropTypes.string,
      {
        [actions.setPersonalNote]: (_, { personalNote }) => personalNote,
        [actions.restart]: () => ''
      }
    ],
    fullName: [
      '',
      PropTypes.string,
      {
        [actions.setFullName]: (_, { fullName }) => fullName,
        [actions.restart]: () => ''
      }
    ],
    email: [
      '',
      PropTypes.string,
      {
        [actions.setEmail]: (_, { email }) => email,
        [actions.restart]: () => ''
      }
    ],
    phone: [
      '',
      PropTypes.string,
      {
        [actions.setPhone]: (_, { phone }) => phone,
        [actions.restart]: () => ''
      }
    ],
    phoneValid: [
      false,
      PropTypes.bool,
      {
        [actions.setPhone]: (_, { valid }) => valid,
        [actions.restart]: () => false
      }
    ],
    serverErrors: [
      {},
      PropTypes.object,
      {
        [actions.submitFailure]: (_, { errors }) => errors,
        [actions.submitRequest]: () => ({}),
        [actions.restart]: () => ({}),
        [actions.setPersonalNote]: () => ({}),
        [actions.setFullName]: () => ({}),
        [actions.setEmail]: () => ({}),
        [actions.setPhone]: () => ({})
      }
    ],
    showErrors: [
      false,
      PropTypes.bool,
      {
        [actions.restart]: () => false,
        [actions.submitFailure]: () => true,
        [actions.submitSuccess]: () => false
      }
    ],
    isSubmitting: [
      false,
      PropTypes.bool,
      {
        [actions.submitRequest]: () => true,
        [actions.submitSuccess]: () => false,
        [actions.submitFailure]: () => false,
        [actions.stopSubmitting]: () => false
      }
    ],
    openedModalAfterSubmit: [
      false,
      PropTypes.bool,
      {
        [actions.stopSubmitting]: () => true
      }
    ],
    fetchValue: [
      '',
      PropTypes.string,
      {
        [actions.getSuggestions]: (state, { value }) => (value === '' ? '' : state),
        [actions.setSuggestions]: (_, { value }) => value
      }
    ],
    rawSuggestions: [
      [],
      PropTypes.array,
      {
        [actions.setSuggestions]: (_, { suggestions }) => suggestions
      }
    ],
    categorySuggestions: [null, PropTypes.array, {}],
    isFetchingSuggestions: [
      false,
      PropTypes.bool,
      {
        [actions.setFetchingSuggestionsStatus]: (_, { bool }) => bool
      }
    ]
  }),

  selectors: ({ selectors, selector }) => ({
    pageKeyword: [() => [selectors.page], page => PAGES[page - 1], PropTypes.string],
    pageIsValid: [
      () => [selectors.page, selector],
      (page, root) => VALIDATORS[page] && !!VALIDATORS[page](root),
      PropTypes.bool
    ],
    progressPercentage: [
      () => [selectors.page],
      page => Math.min(Math.max((100 * (page - 2)) / (PAGES.length - 3), 0), 100),
      PropTypes.number
    ],
    validationErrors: [
      () => [
        selectors.personalNote,
        selectors.fullName,
        selectors.email,
        selectors.phone,
        selectors.phoneValid,
        currentUserIdSelector
      ],
      (personalNote, fullName, email, phone, phoneValid, currentUserId) => {
        const errors = {}

        if (personalNote.trim().length === 0) {
          errors.personalNote = I18n.t('courses.form.error_field_empty')
        }

        if (!currentUserId) {
          if (fullName.trim().split(' ').length < 2) {
            errors.fullName = I18n.t('devise.sessions.new_student.error_no_full_name')
          }

          if (!phone) {
            errors.phone = I18n.t('courses.form.error_field_empty')
          } else if (!phoneValid) {
            errors.phone = I18n.t('errors.invalid_phone_number')
          }

          if (email.trim().length === 0) {
            errors.email = I18n.t('courses.form.error_field_empty')
          } else if (!isEmailValid(email)) {
            errors.email = I18n.t('courses.show_share.email_error_not_email')
          }
        }

        return errors
      }
    ],
    errors: [
      () => [selectors.showErrors, selectors.validationErrors, selectors.serverErrors],
      (showErrors, validationErrors, serverErrors) =>
        showErrors ? Object.assign({}, validationErrors, serverErrors) : {},
      PropTypes.object
    ],
    isValid: [
      () => [selectors.serverErrors, selectors.validationErrors],
      (serverErrors, validationErrors) => Object.keys(serverErrors).length + Object.keys(validationErrors).length === 0,
      PropTypes.bool
    ],
    suggestions: [
      () => [selectors.category, selectors.fetchValue, selectors.rawSuggestions, selectors.categorySuggestions],
      (category, fetchValue, rawSuggestions, categorySuggestions) => {
        let suggestions = fetchValue ? rawSuggestions : categorySuggestions
        if (fetchValue && suggestions && suggestions.length === 0) {
          suggestions = [
            {
              name: I18n.t('react.request.category.activity_select_search_missing', { text: fetchValue }),
              text: fetchValue,
              withText: true,
              slug: null,
              blank: true
            }
          ]
        }

        return suggestions
      },
      PropTypes.array
    ],
    sections: [
      () => [selectors.suggestions],
      suggestions => [
        {
          suggestions
        }
      ],
      PropTypes.array
    ]
  }),

  takeLatest: ({ actions }) => ({
    * [actions.getSuggestions] (action) {
      const { setSuggestions, setFetchingSuggestionsStatus } = this.actions
      const { value } = action.payload

      yield put(setFetchingSuggestionsStatus(true))

      yield delay(50)

      const { suggestions } = yield controller.getSuggestions({ term: value })

      if (suggestions) {
        yield put(setSuggestions({ suggestions, value }))
        yield put(setFetchingSuggestionsStatus(false))
      }
    }
  }),

  takeEvery: ({ actions, workers }) => ({
    [actions.submitRequest]: workers.submit
  }),

  workers: {
    * submit () {
      const { submitSuccess, submitFailure } = this.actions
      const validationErrors = yield this.get('validationErrors')

      // do nothing if we have validation errors
      if (validationErrors && Object.keys(validationErrors).length > 0) {
        yield put(submitFailure({}))
        return
      }

      const values = yield this.fetch(
        'availability',
        'availabilityFlexible',
        'category',
        'location',
        'studentAge',
        'startTime',
        'locationType',
        'frequency',
        'personalNote',
        'fullName',
        'email',
        'phone'
      )

      try {
        const response = yield controller.submitRequest(values)

        if (response.errors) {
          yield put(submitFailure(response.errors))
        } else if (response.redirect_to_signup) {
          window.location.href = response.redirect_path
        } else {
          yield put(submitSuccess())
        }
      } catch {
        messg.error(I18n.t('modals.error_text'), 4000)
        yield put(submitFailure({}))
      }
    }
  }
})
